<template>
  <div class="translation-review">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <a-button type="text" @click="goBack" class="back-btn">
          <template #icon><ArrowLeftOutlined /></template>
          {{ $t('common.back') }}
        </a-button>
      </div>
      <div class="header-right">
        <a-space>
          <a-button @click="saveProgress" :loading="saving" :disabled="isEditingDisabled">
            <template #icon><SaveOutlined /></template>
            {{ $t('video.translationReviewPage.saveProgress') }}
          </a-button>
          <a-button
            v-if="translationProgress?.status === 'completed'"
            type="primary"
            @click="exportSubtitles"
            :loading="exporting"
          >
            <template #icon><ExportOutlined /></template>
            {{ $t('video.translationReviewPage.exportSubtitles') }}
          </a-button>

          <WorkItemActionBar
            v-if="projectId"
            :work-item-id="projectId"
            :current-user-id="userStore.userInfo?.id || userStore.user?.id || 0"
          />
        </a-space>
      </div>
    </div>

    <!-- 工作流进度 -->
    <div class="workflow-steps-container">
      <VideoPipelineSteps :current="currentStep" class="workflow-steps" />
    </div>

    <ReviseCommentBanner
      :task-id="currentReviewTaskId"
      :round="currentReviewRound"
    />

    <!-- 主要内容区域 -->
    <div class="main-content">
      <a-row :gutter="16">
        <!-- 左侧：字幕编辑区 -->
        <a-col :span="18">
          <a-card class="subtitle-card">
            <template #title>
              <div class="card-header">
                <div class="card-title-section">
                  <span class="card-title">{{ $t('video.translationReviewPage.subtitleTranslation') }}</span>
                  <!-- 字符统计 -->
                  <div class="character-stats-inline" v-if="translationStats">
                    <span class="stats-item">
                      <span class="stats-label">{{ $t('video.translationReviewPage.originalCharacters') }}</span>
                      <span class="stats-value">{{ translationStats.original_characters || 0 }}</span>
                    </span>
                    <span class="stats-item">
                      <span class="stats-label">{{ $t('video.translationReviewPage.translatedCharacters') }}</span>
                      <span class="stats-value">{{ translationStats.translated_characters || 0 }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <template #extra>
              <a-space size="middle">
                <a-input 
                  :value="currentTargetLanguage" 
                  :placeholder="$t('video.translationReviewPage.targetLanguagePlaceholder')"
                  style="width: 120px"
                  disabled
                  readonly
                />
                <a-button 
                  type="primary" 
                  @click="startTranslation" 
                  :loading="translating"
                  v-if="showStartTranslationButton"
                >
                  {{ $t('video.translationReviewPage.startTranslation') }}
                </a-button>
              </a-space>
            </template>

            <!-- 字幕列表 -->
            <div class="subtitle-list" v-if="subtitles.length > 0">
              <div 
                v-for="(subtitle, index) in subtitles" 
                :key="subtitle.id"
                class="subtitle-item"
                :class="{ 'active': activeSubtitleIndex === index }"
                @click="setActiveSubtitle(index)"
              >
                <div class="subtitle-time">
                  {{ formatTime(subtitle.start_time) }} --> {{ formatTime(subtitle.end_time) }}
                </div>
                <div class="subtitle-content">
                  <div class="original-text">
                    <label>{{ $t('video.translationReviewPage.originalTextLabel') }}</label>
                    <span>{{ subtitle.original_text }}</span>
                  </div>
                  <div class="translated-text">
                    <div class="translation-header">
                      <label>{{ $t('video.translationReviewPage.translatedTextLabel') }}</label>
                      <a-checkbox 
                        v-model:checked="subtitle.showMultiline"
                        @change="onMultilineToggle(subtitle)"
                        :disabled="isEditingDisabled"
                        class="multiline-checkbox"
                        size="small"
                      >
                        {{ $t('video.translationReviewPage.multiline') }}
                      </a-checkbox>
                    </div>
                    <a-textarea
                      v-model:value="subtitle.translated_text"
                      :rows="1"
                      :placeholder="isEditingDisabled ? $t('video.translationReviewPage.completedReadOnly') : $t('video.translationReviewPage.machineTranslationEditable')"
                      :disabled="isEditingDisabled"
                      @change="onSubtitleEdit(subtitle, 'single')"
                      class="translation-input-main"
                    />
                    <a-textarea
                      v-if="subtitle.showMultiline"
                      v-model:value="subtitle.translated_text_multiline"
                      :rows="2"
                      :placeholder="isEditingDisabled ? $t('video.translationReviewPage.completedReadOnly') : $t('video.translationReviewPage.multilineVersionHint')"
                      :disabled="isEditingDisabled"
                      @change="onSubtitleEdit(subtitle, 'multiline')"
                      class="translation-input-multiline"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <a-empty v-else :description="$t('video.translationReviewPage.noSubtitleData')" />
          </a-card>
        </a-col>

        <!-- 右侧：热词管理区 -->
        <a-col :span="6">
          <a-card :title="$t('video.translationReviewPage.hotwordManagement')" class="hotword-card">
            <template #extra>
              <a-space>
                <a-button 
                  type="primary" 
                  size="small" 
                  @click="extractHotWords" 
                  :loading="extractingHotWords"
                  :disabled="isEditingDisabled"
                  v-if="showExtractHotWordsButton"
                >
                  {{ $t('video.translationReviewPage.extractHotWords') }}
                </a-button>
                <a-button 
                  type="dashed" 
                  size="small" 
                  @click="showAddHotWordModal"
                  :disabled="isEditingDisabled"
                >
                  <template #icon><PlusOutlined /></template>
                </a-button>
                <a-button size="small" @click="refreshHotWords" :disabled="isEditingDisabled">
                  <template #icon><ReloadOutlined /></template>
                </a-button>
              </a-space>
            </template>

            <!-- 热词统计 -->
            <div class="hotword-stats" v-if="hotWordStats">
              <a-row :gutter="4">
                <a-col :span="8">
                  <a-statistic :title="$t('video.translationReviewPage.hotwordTotal')" :value="hotWordStats.total_count" />
                </a-col>
                <a-col :span="8">
                  <a-statistic :title="$t('video.translationReviewPage.hotwordAiSuggested')" :value="hotWordStats.ai_suggested_count" />
                </a-col>
                <a-col :span="8">
                  <a-statistic :title="$t('video.translationReviewPage.hotwordDictionaryMatched')" :value="hotWordStats.from_dictionary_count" />
                </a-col>
              </a-row>
            </div>

            <a-divider style="margin: 8px 0;" />

            <!-- 热词列表 -->
            <div class="hotword-list">
              <a-table 
                :dataSource="hotWords" 
                :columns="hotWordColumns"
                :pagination="false"
                size="small"
                :scroll="{ y: 500 }"
                rowKey="id"
              >
                <template #bodyCell="{ column, record }">
                  <!-- 热词列 -->
                  <template v-if="column.key === 'original_word'">
                    <span class="hotword-text">{{ record.original_word }}</span>
                  </template>
                  
                  <!-- 翻译列 -->
                  <template v-if="column.key === 'translated_word'">
                    <a-input
                      v-if="editingHotWordId === record.id"
                      v-model:value="editingHotWordText"
                      size="small"
                      @pressEnter="saveHotWordEdit(record)"
                      @blur="handleHotWordBlur(record)"
                      ref="hotWordInput"
                      style="width: 100%"
                    />
                    <span 
                      v-else 
                      @click="!isEditingDisabled && startHotWordEdit(record)"
                      :class="['translated-word-clickable', { 'disabled': isEditingDisabled }]"
                    >
                      {{ record.translated_word || (isEditingDisabled ? $t('video.translationReviewPage.notEditable') : $t('video.translationReviewPage.clickToEdit')) }}
                    </span>
                  </template>
                  
                  <!-- 状态列 -->
                  <template v-if="column.key === 'status'">
                    <a-tag size="small" :color="getHotWordStatusColor(record.status)">
                      {{ getHotWordStatusText(record.status) }}
                    </a-tag>
                  </template>
                  
                  <!-- 操作列 -->
                  <template v-if="column.key === 'actions'">
                    <a-space size="small">
                      <a-button 
                        type="text" 
                        size="small" 
                        danger 
                        @click="deleteHotWordItem(record)"
                        :disabled="isEditingDisabled"
                        :icon="h(DeleteOutlined)"
                      />
                    </a-space>
                  </template>
                </template>
              </a-table>
            </div>

            <!-- 空状态 -->
            <a-empty v-if="hotWords.length === 0" :description="$t('video.translationReviewPage.noHotwordData')" size="small" />
            
            <!-- 确认按钮 - 右对齐，向下移动 -->
            <div style="margin-top: 20px; text-align: right;">
              <a-button 
                type="primary" 
                @click="batchConfirmHotWords"
                :loading="batchConfirming"
                size="small"
                style="min-width: 80px;"
              >
                <template #icon><CheckOutlined /></template>
                {{ $t('common.confirm') }}
              </a-button>
            </div>
          </a-card>

        </a-col>
      </a-row>
    </div>

    <!-- 导出对话框 -->
    <a-modal
      v-model:open="exportModalVisible"
      :title="$t('video.translationReviewPage.exportModalTitle')"
      @ok="handleExport"
      :confirm-loading="exporting"
    >
      <a-form :model="exportForm" layout="vertical">
        <a-form-item :label="$t('video.translationReviewPage.subtitleType')">
          <a-radio-group v-model:value="exportForm.subtitleType">
            <a-radio value="original">{{ $t('video.translationReviewPage.originalSubtitle') }}</a-radio>
            <a-radio value="translated">{{ $t('video.translationReviewPage.translatedSubtitle') }}</a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item :label="$t('video.translationReviewPage.exportFormat')">
          <a-select v-model:value="exportForm.format">
            <a-select-option value="srt">{{ $t('video.translationReviewPage.formatSrt') }}</a-select-option>
            <a-select-option value="webvtt">{{ $t('video.translationReviewPage.formatWebvtt') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('video.translationReviewPage.versionSelection')">
          <a-checkbox-group v-model:value="exportForm.versions">
            <a-checkbox value="single_line">{{ $t('video.translationReviewPage.singleLineVersion') }}</a-checkbox>
            <a-checkbox 
              value="multi_line" 
              :disabled="exportForm.subtitleType === 'original'"
            >
              {{ $t('video.translationReviewPage.multiLineVersion') }}
            </a-checkbox>
          </a-checkbox-group>
          <div v-if="exportForm.subtitleType === 'original'" class="export-tip">
            <a-typography-text type="secondary" style="font-size: 12px;">
              {{ $t('video.translationReviewPage.originalSubtitleTip') }}
            </a-typography-text>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 手动添加热词对话框 -->
    <a-modal
      v-model:open="addHotWordModalVisible"
      :title="$t('video.translationReviewPage.addHotwordTitle')"
      @ok="handleAddHotWord"
      @cancel="handleCancelAddHotWord"
      :confirm-loading="addingHotWord"
      width="500px"
    >
      <a-form :model="newHotWordForm" layout="vertical">
        <a-form-item 
          :label="$t('video.translationReviewPage.originalHotword')"
          name="original_word"
          :rules="[{ required: true, message: $t('video.translationReviewPage.enterOriginalHotword') }]"
        >
          <a-input 
            v-model:value="newHotWordForm.original_word" 
            :placeholder="$t('video.translationReviewPage.enterOriginalHotwordPlaceholder')"
            :maxlength="100"
          />
        </a-form-item>
        <a-form-item 
          :label="$t('video.translationReviewPage.hotwordTranslation')"
          name="translated_word"
          :rules="[{ required: true, message: $t('video.translationReviewPage.enterHotwordTranslation') }]"
        >
          <a-input 
            v-model:value="newHotWordForm.translated_word" 
            :placeholder="$t('video.translationReviewPage.enterHotwordTranslationPlaceholder')"
            :maxlength="100"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, computed, nextTick, h } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { 
  ArrowLeftOutlined, 
  SaveOutlined, 
  ExportOutlined,
  ReloadOutlined,
  CheckOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  PlusOutlined
} from '@ant-design/icons-vue'
import { 
  startSubtitleTranslation,
  getTranslationProgress,
  extractSubtitleHotWords,
  getTranslatedSubtitles,
  updateSubtitleTranslation,
  saveTranslationProgress,
  getTranslationStatistics,
  exportTranslatedSubtitles,
  completeTranslationTask,
  type SubtitleTranslationRequest,
  type HotWordExtractionRequest,
  type TranslatedSubtitleResponse,
  type HotWordResponse,
  type SubtitleTranslationProgress,
  type TranslationStatistics
} from '@/modules/video/api'

// 扩展TranslatedSubtitleResponse类型以包含showMultiline属性
interface ExtendedSubtitleResponse extends TranslatedSubtitleResponse {
  showMultiline?: boolean
}
import {
  getProjectHotWords,
  getHotWordStats,
  updateHotWord,
  updateHotWordStatus,
  deleteHotWord,
  type HotWord,
  type HotWordStats
} from '@/modules/system/api/hotword'
import WorkItemActionBar from '@/modules/project/components/WorkItemActionBar.vue'
import ReviseCommentBanner from '@/modules/task/components/ReviseCommentBanner.vue'
import { getWorkItem } from '@/modules/project/api/work-item'
import { subtitleApi } from '@/modules/video/api'
import {
  getVideoWorkspace,
  type VideoWorkspaceView,
} from '@/modules/video/utils/video-workspace'
import { shouldAutoStartSubtitleMachineTranslation } from '@/modules/video/utils/should-auto-start-subtitle-mt'
import { runApiBatch } from '@/shared/api/use-api-batch'
import VideoPipelineSteps from '@/modules/video/components/VideoPipelineSteps.vue'
import { VIDEO_PIPELINE_STEP } from '@/modules/video/constants/video-pipeline-steps'
// 任务通知已由后端自动发送，不再需要前端调用
import { useUserStore } from '@/stores'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { t, locale } = useI18n()

// 基础数据
const projectId = computed(() => Number(route.params.id))
const currentReviewTaskId = ref<number | null>(null)
const currentReviewRound = ref(0)
const projectInfo = ref<VideoWorkspaceView | null>(null)
const currentTargetLanguage = ref<string>('')

async function loadReviewTaskContext() {
  if (!projectId.value) return
  try {
    const wi = await getWorkItem(projectId.value)
    currentReviewTaskId.value = wi.current_task_id ?? wi.current_task?.id ?? null
    currentReviewRound.value = wi.current_task?.round ?? 0
  } catch (e) {
    console.error(e)
    currentReviewTaskId.value = null
    currentReviewRound.value = 0
  }
}

// 翻译相关状态
const translating = ref(false)
const translationStarted = ref(false)
const translationProgress = ref<SubtitleTranslationProgress | null>(null)
const subtitles = ref<ExtendedSubtitleResponse[]>([])
const activeSubtitleIndex = ref(0)
const progressPollTimer = ref<NodeJS.Timeout | null>(null) // 轮询定时器引用

// 热词相关状态
const extractingHotWords = ref(false)
const hotWords = ref<HotWord[]>([]) // 确保初始值是空数组
const hotWordStats = ref<HotWordStats | null>(null)
const editingHotWordId = ref<number | null>(null)
const editingHotWordText = ref('')
const batchConfirming = ref(false)

// 手动添加热词相关状态
const addHotWordModalVisible = ref(false)
const addingHotWord = ref(false)
const newHotWordForm = reactive({
  original_word: '',
  translated_word: ''
})

// 热词表格列定义
const hotWordColumns = computed(() => [
  {
    title: t('video.translationReviewPage.hotwordColumn'),
    dataIndex: 'original_word',
    key: 'original_word',
    width: 80,
    ellipsis: true
  },
  {
    title: t('video.translationReviewPage.translationColumn'),
    dataIndex: 'translated_word',
    key: 'translated_word',
    width: 120,
    ellipsis: true
  },
  {
    title: t('video.translationReviewPage.statusColumn'),
    dataIndex: 'status',
    key: 'status',
    width: 80,
    align: 'center' as const
  },
  {
    title: t('video.translationReviewPage.actionsColumn'),
    key: 'actions',
    width: 50,
    align: 'center' as const
  }
])

// 计算属性：是否有未确认的热词
const hasUnconfirmedHotWords = computed(() => {
  return hotWords.value.some(hw => hw.status !== 'confirmed')
})

// 计算属性：当前工作流步骤（返回索引，从0开始）
const currentStep = computed(() => {
  if (translationProgress.value?.status === 'completed') {
    return VIDEO_PIPELINE_STEP.COMPLETE_OR_ASSIGN
  }
  return VIDEO_PIPELINE_STEP.AI_TRANSLATION
})

// 计算属性：是否禁用编辑（任务完成后禁用编辑）
const isEditingDisabled = computed(() => {
  return translationProgress.value?.status === 'completed'
})

// 计算属性：是否显示开始翻译按钮（没有译文时显示）
const showStartTranslationButton = computed(() => {
  // 在pending或failed状态且没有译文时显示
  const allowedStatuses = ['pending', 'failed']
  if (!translationProgress.value?.status || !allowedStatuses.includes(translationProgress.value.status)) {
    return false
  }
  
  // 检查是否有任何字幕已经有译文
  const hasTranslatedText = subtitles.value.some(subtitle => 
    subtitle.translated_text && subtitle.translated_text.trim() !== ''
  )
  
  return !hasTranslatedText
})

// 计算属性：是否显示提取热词按钮（始终隐藏，因为会自动提取）
const showExtractHotWordsButton = computed(() => {
  // 始终隐藏提取热词按钮，因为会在翻译完成后自动提取
  return false
})

// 统计信息
const translationStats = ref<TranslationStatistics | null>(null)

// 保存和导出
const saving = ref(false)
const exporting = ref(false)
const completing = ref(false)
const exportModalVisible = ref(false)
const exportForm = reactive({
  format: 'srt',
  versions: ['single_line', 'multi_line'],
  subtitleType: 'translated' // 新增：字幕类型
})

// 自动保存定时器
let autoSaveTimer: NodeJS.Timeout | null = null

// 生命周期
onMounted(async () => {
  void loadReviewTaskContext()
  await loadProjectInfo()
  
  if (projectInfo.value?.target_languages?.length) {
    // 直接使用语言代码
    currentTargetLanguage.value = projectInfo.value.target_languages[0]
    await loadTranslationData()
  } else {
    console.warn('项目没有目标语言，无法加载翻译数据')
  }
  
  // 启动自动保存
  startAutoSave()
  
  // 添加键盘快捷键
  document.addEventListener('keydown', handleKeydown)
})

// 组件卸载时清理
onUnmounted(() => {
  stopAutoSave()
  if (progressPollTimer.value) {
    clearInterval(progressPollTimer.value)
    progressPollTimer.value = null
  }
  document.removeEventListener('keydown', handleKeydown)
})

// 加载项目信息
const loadProjectInfo = async () => {
  try {
    projectInfo.value = await getVideoWorkspace(projectId.value)
  } catch (error) {
    console.error('加载项目信息失败:', error)
  }
}

// 加载目标语言选项（已移除，因为目标语言不可编辑）

// 加载翻译数据
const loadTranslationData = async () => {
  if (!currentTargetLanguage.value) {
    return
  }
  
  try {
    // 先加载项目信息，检查项目状态
    await loadProjectInfo()
    
    // 仅按 pipeline_stage 自动开翻；勿用 status（被指派人 Task=processing 会误触发「翻译完成」）
    if (
      shouldAutoStartSubtitleMachineTranslation(
        projectInfo.value?.pipeline_stage,
        translationStarted.value,
      )
    ) {
      console.log('条目尚未机翻就绪，自动开始翻译')
      await startTranslation()
      return // 翻译完成后会自动刷新数据
    }
    
    // 加载翻译进度
    const progressResponse = await getTranslationProgress(projectId.value, currentTargetLanguage.value)
    translationProgress.value = progressResponse
    translationStarted.value = progressResponse?.status === 'translating' || progressResponse?.status === 'completed'

    // 先加载原始字幕数据（确保有基础数据）
    await loadSubtitles()
    
    // 如果原始字幕加载成功，尝试加载翻译字幕（合并翻译数据）
    if (subtitles.value.length > 0) {
      try {
        await loadTranslatedSubtitles()
      } catch (error) {
        // 如果加载翻译字幕失败，保留原始字幕数据
        console.warn('加载翻译字幕失败，使用原始字幕:', error)
        // 不显示错误消息，因为原始字幕已经加载成功
      }
    }
    
    // 只加载现有热词数据，不自动提取
    await loadHotWords()
    
    // 始终加载翻译统计数据（字幕加载后再加载统计，确保能从前端计算）
    await loadTranslationStats()
    
    // 检查是否有翻译数据（检查字幕中是否有 translated_text）
    // 这是判断翻译是否存在的唯一可靠方法，不依赖状态
    const hasTranslatedData = subtitles.value.some(subtitle => 
      subtitle.translated_text && subtitle.translated_text.trim() !== ''
    )
    
    // 判断翻译是否存在的标准：检查是否有翻译数据
    // 不依赖状态（因为 pending 是项目状态，不是翻译状态）
    const hasHotWords = hotWords.value.length > 0
    // 检查是否正在提取热词（防止重复调用）
    const isExtracting = extractingHotWords.value
    // 检查翻译进度中是否显示正在提取热词
    const isExtractingInProgress = translationProgress.value?.status === 'extracting_hot_words'
    // 只要有翻译数据（翻译存在），且没有热词，且不在提取中，就自动提取热词
    const shouldExtract = hasTranslatedData && !hasHotWords && !isExtracting && !isExtractingInProgress
    
    if (shouldExtract) {
      // 不等待热词提取完成，让它后台运行
      autoExtractHotWords().catch(error => {
        console.error('后台热词提取失败:', error)
      })
    }
  } catch (error) {
    console.error('加载翻译数据失败:', error)
  }
}

// 加载原始字幕数据
const loadSubtitles = async () => {
  try {
    const response = await subtitleApi.getProjectSubtitles(projectId.value)
    // 处理API响应格式：可能是 { items: [] } 或直接是数组
    const subtitlesData = Array.isArray(response) ? response : ((response as any).items || [])
    
    if (subtitlesData.length === 0) {
      console.warn('项目没有字幕数据')
      subtitles.value = []
      return
    }
    
    // 将原始字幕数据转换为翻译字幕格式
    subtitles.value = subtitlesData.map((subtitle: any) => ({
      id: subtitle.id,
      start_time: subtitle.start_time,
      end_time: subtitle.end_time,
      original_text: subtitle.original_text || '', // 使用正确的字段名
      translated_text: subtitle.translated_text || '', // 使用API返回的翻译文本
      translated_text_multiline: subtitle.translated_text_multiline || '', // 使用API返回的分行版本
      language: subtitle.language || 'zh', // 默认语言
      order_index: subtitle.order_index || 0, // 默认顺序
      character_count: subtitle.original_text?.length || 0, // 字符数
      showMultiline: !!subtitle.translated_text_multiline // 根据是否有分行内容决定是否显示分行
    }))
    
    console.log(`成功加载 ${subtitles.value.length} 条原始字幕`)
  } catch (error) {
    console.error('Load subtitles error:', error)
    subtitles.value = []
  }
}

// 加载翻译后的字幕（合并到现有字幕数据中）
const loadTranslatedSubtitles = async () => {
  try {
    const response = await getTranslatedSubtitles(projectId.value, currentTargetLanguage.value)
    const translatedSubtitles = response.subtitles || []
    
    if (translatedSubtitles.length === 0) {
      console.log('没有翻译字幕数据，保留原始字幕')
      return
    }
    
    // 将翻译字幕数据合并到现有字幕中
    // 创建一个以字幕ID为key的映射，用于快速查找
    const subtitleMap = new Map(subtitles.value.map(s => [s.id, s]))
    
    // 更新现有字幕的翻译数据
    translatedSubtitles.forEach((translatedSubtitle: any) => {
      const existingSubtitle = subtitleMap.get(translatedSubtitle.id)
      if (existingSubtitle) {
        // 更新翻译文本
        existingSubtitle.translated_text = translatedSubtitle.translated_text || existingSubtitle.translated_text || ''
        existingSubtitle.translated_text_multiline = translatedSubtitle.translated_text_multiline || existingSubtitle.translated_text_multiline || ''
        existingSubtitle.showMultiline = !!translatedSubtitle.translated_text_multiline
      } else {
        // 如果字幕不存在，添加新字幕（这种情况应该很少见）
        subtitleMap.set(translatedSubtitle.id, {
          id: translatedSubtitle.id,
          start_time: translatedSubtitle.start_time,
          end_time: translatedSubtitle.end_time,
          original_text: translatedSubtitle.original_text || '',
          translated_text: translatedSubtitle.translated_text || '',
          translated_text_multiline: translatedSubtitle.translated_text_multiline || '',
          language: translatedSubtitle.language || currentTargetLanguage.value,
          order_index: translatedSubtitle.order_index || 0,
          character_count: translatedSubtitle.character_count || 0,
          showMultiline: !!translatedSubtitle.translated_text_multiline
        })
      }
    })
    
    // 将Map转换回数组，并按order_index排序
    subtitles.value = Array.from(subtitleMap.values()).sort((a, b) => 
      (a.order_index || 0) - (b.order_index || 0)
    )
    
    console.log(`成功合并 ${translatedSubtitles.length} 条翻译字幕数据`)
  } catch (error) {
    // 错误已在调用处处理，这里只记录日志
    console.error('Load translated subtitles error:', error)
    throw error // 重新抛出错误，让调用者知道加载失败
  }
}

// 加载翻译统计
const loadTranslationStats = async () => {
  try {
    const response = await getTranslationStatistics(projectId.value, currentTargetLanguage.value)
    
    // 字段名映射：处理后端返回的旧字段名
    let originalChars = response.original_characters || (response as any).total_characters || 0
    let translatedChars = response.translated_characters || 0
    
    // 如果API返回的统计为0，但前端有字幕数据，则从前端计算
    if (originalChars === 0 && subtitles.value.length > 0) {
      originalChars = subtitles.value.reduce((sum, s) => {
        return sum + (s.original_text?.length || 0)
      }, 0)
      console.log(`API返回原文字符数为0，从前端计算得到: ${originalChars}`)
    }
    
    if (translatedChars === 0 && subtitles.value.length > 0) {
      translatedChars = subtitles.value.reduce((sum, s) => {
        return sum + (s.translated_text?.length || 0)
      }, 0)
    }
    
    translationStats.value = {
      project_id: response.project_id,
      target_language: response.target_language,
      original_characters: originalChars,
      translated_characters: translatedChars
    }
  } catch (error) {
    console.error('Load translation stats error:', error)
    // API调用失败时，从前端字幕数据计算统计
    if (subtitles.value.length > 0) {
      const originalChars = subtitles.value.reduce((sum, s) => {
        return sum + (s.original_text?.length || 0)
      }, 0)
      const translatedChars = subtitles.value.reduce((sum, s) => {
        return sum + (s.translated_text?.length || 0)
      }, 0)
      
      translationStats.value = {
        project_id: projectId.value,
        target_language: currentTargetLanguage.value,
        original_characters: originalChars,
        translated_characters: translatedChars
      }
      console.log(`统计API调用失败，从前端计算: 原文=${originalChars}, 译文=${translatedChars}`)
    }
  }
}

// 加载热词数据
const loadHotWords = async () => {
  try {
    const results = await runApiBatch([
      (ctx) =>
        getProjectHotWords(
          projectId.value,
          {
            target_language: currentTargetLanguage.value,
            page_size: 100,
          },
          ctx.requestConfig,
        ),
      (ctx) =>
        getHotWordStats(
          projectId.value,
          currentTargetLanguage.value,
          ctx.requestConfig,
        ),
    ])
    if (results[0]?.status === 'rejected') {
      throw results[0].reason
    }
    if (results[1]?.status === 'rejected') {
      throw results[1].reason
    }
    hotWords.value = results[0].value.items || []
    hotWordStats.value = results[1].value
  } catch (error) {
    console.error('Load hot words error:', error)
    hotWords.value = hotWords.value || []
  }
}

// 只加载热词统计数据
const loadHotWordStats = async () => {
  try {
    const statsResponse = await getHotWordStats(projectId.value, currentTargetLanguage.value)
    hotWordStats.value = statsResponse
  } catch (error) {
    console.error('Load hot word stats error:', error)
  }
}

// 开始翻译
const startTranslation = async () => {
  if (!currentTargetLanguage.value) {
    message.warning(t('video.translationReviewPage.warnSelectTargetLanguage'))
    return
  }


  translating.value = true
  try {
    const request: SubtitleTranslationRequest = {
      target_language: currentTargetLanguage.value
    }
    
    console.log('开始同步翻译...')
    // 同步翻译（等待翻译完成）
    await startSubtitleTranslation(projectId.value, request)
    translationStarted.value = true
    message.success(t('video.translationReviewPage.translationCompleted'))
    
    // 翻译完成后，重新加载数据
    await loadTranslationData()
    
    // 并行启动热词提取（不等待翻译完成）
    // 先检查是否已有热词，如果没有则自动提取
    await loadHotWords()
    if (hotWords.value.length === 0) {
      // 不等待热词提取完成，让它后台运行
      autoExtractHotWords().catch(error => {
        console.error('后台热词提取失败:', error)
      })
    }
  } catch (error: any) {
    console.error('Start translation error:', error)
  } finally {
    translating.value = false
  }
}

// 轮询翻译进度（已废弃，翻译改为同步执行）
// 保留此函数以防需要，但不再使用
const pollTranslationProgress = () => {
  // 翻译已改为同步执行，不再需要轮询
  console.log('翻译已改为同步执行，不再需要轮询')
}

// 目标语言变更（已移除，因为目标语言不可编辑）

// 设置活动字幕
const setActiveSubtitle = (index: number) => {
  activeSubtitleIndex.value = index
}

// 字幕编辑
const onSubtitleEdit = async (subtitle: ExtendedSubtitleResponse, type: 'single' | 'multiline') => {
  try {
    // 根据编辑类型决定传递哪些参数
    const translatedText = subtitle.translated_text || ''
    // 编辑单行时，不更新分行版本（传递undefined）
    // 编辑分行时，传递分行版本内容
    const translatedTextMultiline = type === 'multiline' 
      ? subtitle.translated_text_multiline 
      : undefined
    
    await updateSubtitleTranslation(
      projectId.value, 
      subtitle.id, 
      translatedText, 
      translatedTextMultiline
    )
    
    await loadTranslationStats()
  } catch (error) {
    console.error('Update subtitle error:', error)
  }
}

// 分行复选框切换
const onMultilineToggle = async (subtitle: ExtendedSubtitleResponse) => {
  if (!subtitle.showMultiline) {
    // 取消分行时，将第一行(translated_text)与第二行(translated_text_multiline)合并回单行
    const line1 = subtitle.translated_text?.trim() || ''
    const line2Raw = subtitle.translated_text_multiline?.trim() || ''
    const line2Parts = line2Raw.split(/\n+/).map(s => s.trim()).filter(Boolean)
    const merged = [line1, ...line2Parts].filter(Boolean).join(' ')
    if (merged) {
      subtitle.translated_text = merged
      try {
        await updateSubtitleTranslation(
          projectId.value,
          subtitle.id,
          merged,
          ''
        )
        await loadTranslationStats()
      } catch (error) {
        console.error('Update subtitle error:', error)
      }
    }
    subtitle.translated_text_multiline = ''
  }
}

// 检查并自动提取热词
const checkAndExtractHotWords = async () => {
  if (!currentTargetLanguage.value) return
  
  try {
    // 先尝试加载现有热词
    await loadHotWords()
    
    // 如果没有热词，自动提取
    if (hotWords.value.length === 0) {
      await autoExtractHotWords()
    }
  } catch (error) {
    console.error('检查热词失败:', error)
  }
}

// 自动提取热词（首次进入时或翻译启动时）
const autoExtractHotWords = async () => {
  if (!currentTargetLanguage.value) {
    return
  }
  
  // 检查是否正在提取中（防止重复调用）
  if (extractingHotWords.value) {
    return
  }
  
  // 再次检查是否已有热词（防止并发情况）
  // 注意：调用此函数前应该已经加载过热词，这里只是双重检查
  if (hotWords.value.length > 0) {
    return
  }
  
  extractingHotWords.value = true
  try {
    const request: HotWordExtractionRequest = {
      target_language: currentTargetLanguage.value,
      // top_n 不设置，让后端使用系统配置
      force_extract: false // 不强制提取，优先使用已存在的热词
    }
    
    await extractSubtitleHotWords(projectId.value, request)
    
    // 重新加载热词数据
    await loadHotWords()
  } catch (error) {
    console.error('自动提取热词失败:', error)
  } finally {
    extractingHotWords.value = false
  }
}

// 提取热词
const extractHotWords = async () => {
  if (!currentTargetLanguage.value) {
    message.warning(t('video.translationReviewPage.warnSelectTargetLanguage'))
    return
  }

  extractingHotWords.value = true
  try {
    const request: HotWordExtractionRequest = {
      target_language: currentTargetLanguage.value,
      // top_n 不设置，让后端使用系统配置
      force_extract: false // 手动提取时也不强制，优先使用已存在的热词
    }
    
    const response = await extractSubtitleHotWords(projectId.value, request)
    
    // 重新加载热词数据
    await loadHotWords()
    
    message.success(t('video.translationReviewPage.extractHotWordsSuccess', { count: response.total_count }))
  } catch (error) {
    console.error('Extract hot words error:', error)
  } finally {
    extractingHotWords.value = false
  }
}

// 刷新热词
const refreshHotWords = async () => {
  await loadHotWords()
}

// 开始编辑热词
const startHotWordEdit = (hotWord: HotWord) => {
  editingHotWordId.value = hotWord.id
  editingHotWordText.value = hotWord.translated_word || ''
  
  nextTick(() => {
    const input = document.querySelector('.hotword-item.editing input') as HTMLInputElement
    if (input) {
      input.focus()
      input.select()
    }
  })
}

// 保存热词编辑
const saveHotWordEdit = async (hotWord: HotWord) => {
  try {
    await updateHotWord(hotWord.id, {
      translated_word: editingHotWordText.value,
      status: 'edited'
    })
    
    // 直接更新本地数据，不重新加载
    hotWord.translated_word = editingHotWordText.value
    hotWord.status = 'edited'
    
    editingHotWordId.value = null
    editingHotWordText.value = ''
    
    // 只重新加载统计数据，不重新加载热词列表
    await loadHotWordStats()
    
    message.success(t('video.translationReviewPage.updateHotwordSuccess'))
  } catch (error) {
    console.error('Update hot word error:', error)
  }
}

// 取消编辑热词
const cancelHotWordEdit = () => {
  editingHotWordId.value = null
  editingHotWordText.value = ''
}

// 处理热词编辑失去焦点
const handleHotWordBlur = async (hotWord: HotWord) => {
  // 延迟执行，避免与点击事件冲突
  setTimeout(async () => {
    if (editingHotWordId.value === hotWord.id && editingHotWordText.value.trim()) {
      // 如果有内容变化，自动保存
      await saveHotWordEdit(hotWord)
    } else if (editingHotWordId.value === hotWord.id) {
      // 如果没有内容变化，取消编辑
      cancelHotWordEdit()
    }
  }, 100)
}

// 确认热词
const confirmHotWord = async (hotWord: HotWord) => {
  try {
    // 如果有字典ID，使用新的API更新状态
    if (hotWord.dictionary_id) {
      await updateHotWordStatus(hotWord.dictionary_id, 'confirmed')
    } else {
      // 兼容旧API
      await updateHotWord(hotWord.id, { status: 'confirmed' })
    }
    
    // 只有在API调用成功后才更新本地状态
    hotWord.status = 'confirmed'
    
    // 重新加载热词数据以确保状态同步
    await loadHotWords()
    
    message.success(t('video.translationReviewPage.confirmHotwordSuccess'))
  } catch (error) {
    console.error('Confirm hot word error:', error)
    // API调用失败时不更新本地状态，保持原有状态
  }
}

// 删除热词
const deleteHotWordItem = async (hotWord: HotWord) => {
  try {
    await deleteHotWord(hotWord.id)
    
    const index = hotWords.value.findIndex(hw => hw.id === hotWord.id)
    if (index > -1) {
      hotWords.value.splice(index, 1)
    }
    
    // 重新加载统计数据
    await loadHotWords()
    
    message.success(t('video.translationReviewPage.deleteHotwordSuccess'))
  } catch (error) {
    console.error('Delete hot word error:', error)
  }
}

// 批量确认热词
const batchConfirmHotWords = async () => {
  batchConfirming.value = true
  try {
    let successCount = 0
    let failCount = 0
    
    // 遍历所有热词，将非译员确认状态的热词都设为译员确认
    for (const hotWord of hotWords.value) {
      if (hotWord.status !== 'confirmed') {
        try {
          // 如果有字典ID，使用新的API更新状态
          if (hotWord.dictionary_id) {
            await updateHotWordStatus(hotWord.dictionary_id, 'confirmed')
          } else {
            // 兼容旧API
            await updateHotWord(hotWord.id, { status: 'confirmed' })
          }
          
          // 只有在API调用成功后才更新本地状态
          hotWord.status = 'confirmed'
          successCount++
        } catch (error) {
          console.error(`确认热词 ${hotWord.original_word} 失败:`, error)
          failCount++
          // API调用失败时不更新本地状态，保持原有状态
        }
      }
    }
    
    // 重新加载热词数据以确保状态同步
    await loadHotWords()
    
    if (successCount > 0) {
      if (failCount > 0) {
        message.warning(t('video.translationReviewPage.batchConfirmPartial', { success: successCount, failed: failCount }))
      } else {
        message.success(t('video.translationReviewPage.batchConfirmSuccess', { count: successCount }))
      }
    } else if (failCount > 0) {
      message.error(t('video.translationReviewPage.batchConfirmFailedCount', { count: failCount }))
    } else {
      message.info(t('video.translationReviewPage.allHotwordsConfirmed'))
    }
  } catch (error) {
    console.error('Batch confirm hot words error:', error)
  } finally {
    batchConfirming.value = false
  }
}

// 显示手动添加热词对话框
const showAddHotWordModal = () => {
  newHotWordForm.original_word = ''
  newHotWordForm.translated_word = ''
  addHotWordModalVisible.value = true
}

// 取消添加热词
const handleCancelAddHotWord = () => {
  addHotWordModalVisible.value = false
  newHotWordForm.original_word = ''
  newHotWordForm.translated_word = ''
}

// 手动添加热词
const handleAddHotWord = async () => {
  if (!newHotWordForm.original_word.trim() || !newHotWordForm.translated_word.trim()) {
    message.error(t('video.translationReviewPage.fillHotwordInfo'))
    return
  }

  if (!currentTargetLanguage.value) {
    message.error(t('video.translationReviewPage.warnSelectTargetLanguage'))
    return
  }

  addingHotWord.value = true
  try {
    // 检查热词是否已存在
    const existingHotWord = hotWords.value.find(
      hw => hw.original_word.toLowerCase() === newHotWordForm.original_word.trim().toLowerCase()
    )
    
    if (existingHotWord) {
    message.warning(t('video.translationReviewPage.hotwordExists'))
      return
    }

    // 调用提取热词接口，传入手动添加的热词数据
    const response = await extractSubtitleHotWords(projectId.value, {
      target_language: currentTargetLanguage.value,
      manual_hot_word: {
        original_word: newHotWordForm.original_word.trim(),
        translated_word: newHotWordForm.translated_word.trim()
      }
    } as any)

    if ((response as any).success) {
      // 由于使用了与自动提取相同的处理流程，直接重新加载数据
      await loadHotWords()
      await loadHotWordStats()
    } else {
      throw new Error((response as any).message || t('video.translationReviewPage.addHotwordFailed'))
    }
    
    message.success(t('video.translationReviewPage.addHotwordSuccess'))
    addHotWordModalVisible.value = false
    newHotWordForm.original_word = ''
    newHotWordForm.translated_word = ''
    
  } catch (error) {
    console.error('Add hot word error:', error)
  } finally {
    addingHotWord.value = false
  }
}

// 保存进度
const saveProgress = async () => {
  if (!currentTargetLanguage.value) {
    message.warning(t('video.translationReviewPage.warnSelectTargetLanguage'))
    return
  }

  saving.value = true
  try {
    await saveTranslationProgress(projectId.value, {
      target_language: currentTargetLanguage.value,
      auto_save: false
    })
    
    await loadTranslationStats()
    message.success(t('video.translationReviewPage.saveProgressSuccess'))
  } catch (error) {
    console.error('Save progress error:', error)
  } finally {
    saving.value = false
  }
}

// 导出字幕
const exportSubtitles = () => {
  exportModalVisible.value = true
}

// 完成任务
const completeTranslation = async () => {
  if (!currentTargetLanguage.value) {
    message.warning(t('video.translationReviewPage.warnSelectTargetLanguage'))
    return
  }

  // 确认对话框
  const confirmed = confirm(t('video.translationReviewPage.completeConfirm'))
  if (!confirmed) {
    return
  }

  completing.value = true
  try {
    const response = await completeTranslationTask(
      projectId.value,
      currentTargetLanguage.value
    )
    
    // 任务完成通知已由后端自动发送，前端不再发送
    
    message.success(response.message)
    
    // 更新翻译进度状态
    if (translationProgress.value) {
      translationProgress.value.status = 'completed'
    }
    
    // 翻译完成，停止自动保存
    stopAutoSave()
    
    // 刷新页面数据
    await loadTranslationData()
    
    // 根据任务类型跳转
    if (response.task_type === 'self_completed') {
      // 自己完成：2 秒后跳转到「我的项目」
      setTimeout(() => {
        router.push('/project/my-projects')
      }, 2000)
    } else if (response.task_type === 'assigned') {
      // 被指派人完成：跳转到「收到的任务」（避免 TASK_ASSIGN 权限 403）
      router.push('/task/received')
    }

  } catch (error) {
    console.error('Complete translation error:', error)
  } finally {
    completing.value = false
  }
}

// 处理导出
const handleExport = async () => {
  if (!currentTargetLanguage.value) {
    message.warning(t('video.translationReviewPage.warnSelectTargetLanguage'))
    return
  }

  if (exportForm.versions.length === 0) {
    message.warning(t('video.translationReviewPage.selectExportVersion'))
    return
  }

  // 如果选择原字幕，自动移除分行版本选项
  if (exportForm.subtitleType === 'original') {
    exportForm.versions = exportForm.versions.filter(v => v !== 'multi_line')
  }

  exporting.value = true
  try {
    const response = await exportTranslatedSubtitles(projectId.value, {
      target_language: currentTargetLanguage.value,
      format: exportForm.format,
      version: exportForm.versions.join(','),
      subtitle_type: exportForm.subtitleType
    })
    
    console.log('导出字幕响应:', response)
    
    // 处理文件下载
    if (response && response.files && Array.isArray(response.files) && response.files.length > 0) {
      let downloadCount = 0
      response.files.forEach((file: any) => {
        // 检查文件是否有内容
        if (file.content && file.filename) {
          try {
            // 根据格式设置正确的 MIME 类型
            let mimeType = 'text/plain'
            if (file.filename.endsWith('.srt')) {
              mimeType = 'text/srt'
            } else if (file.filename.endsWith('.vtt')) {
              mimeType = 'text/vtt'
            } else if (file.filename.endsWith('.txt')) {
              mimeType = 'text/plain'
            }
            
            const blob = new Blob([file.content], { type: mimeType })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = file.filename
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            
            // 延迟清理，确保下载开始
            setTimeout(() => {
              document.body.removeChild(link)
              URL.revokeObjectURL(url)
            }, 100)
            
            downloadCount++
            console.log(`文件下载已触发: ${file.filename}`)
          } catch (downloadError) {
            console.error(`下载文件失败 ${file.filename}:`, downloadError)
          }
        } else {
          console.warn('文件缺少内容或文件名:', file)
        }
      })
      
      if (downloadCount === 0) {
        message.warning(t('video.translationReviewPage.noFilesToDownload'))
        console.error('没有可下载的文件，响应数据:', response)
      } else {
        exportModalVisible.value = false
        message.success(t('video.translationReviewPage.exportSuccess', { count: downloadCount }))
      }
    } else {
      console.error('意外的响应格式:', response)
      message.error(t('video.translationReviewPage.exportResponseError'))
    }
  } catch (error: any) {
    console.error('导出字幕错误:', error)
    if (error.response) {
      console.error('错误响应:', error.response.data)
    }
  } finally {
    exporting.value = false
  }
}

// 自动保存
const startAutoSave = () => {
  // 如果已经有定时器，先清除
  if (autoSaveTimer) {
    clearInterval(autoSaveTimer)
    autoSaveTimer = null
  }
  
  autoSaveTimer = setInterval(async () => {
    // 检查是否应该停止自动保存
    // 1. 翻译未开始
    if (!translationStarted.value) {
      console.log('翻译未开始，跳过自动保存')
      return
    }
    
    // 2. 没有目标语言
    if (!currentTargetLanguage.value) {
      console.log('没有目标语言，跳过自动保存')
      return
    }
    
    // 3. 翻译已完成
    if (translationProgress.value?.status === 'completed') {
      console.log('翻译已完成，停止自动保存')
      stopAutoSave()
      return
    }
    
    // 4. 检查是否有未保存的更改（这里可以添加更复杂的逻辑）
    // 目前只要有翻译数据就保存
    
    try {
      console.log('执行自动保存...')
      await saveTranslationProgress(projectId.value, {
        target_language: currentTargetLanguage.value,
        auto_save: true
      })
      console.log('自动保存成功')
    } catch (error) {
      console.error('自动保存错误:', error)
      // 如果保存失败，不停止定时器，下次再试
    }
  }, 30000) // 每30秒自动保存一次
}

// 停止自动保存
const stopAutoSave = () => {
  if (autoSaveTimer) {
    clearInterval(autoSaveTimer)
    autoSaveTimer = null
    console.log('自动保存已停止')
  }
}

// 键盘快捷键处理
const handleKeydown = (e: KeyboardEvent) => {
  // Ctrl+S 保存
  if (e.ctrlKey && e.key === 's') {
    e.preventDefault()
    saveProgress()
  }
  
  // Ctrl+E 导出
  if (e.ctrlKey && e.key === 'e') {
    e.preventDefault()
    exportSubtitles()
  }
  
  // 上下箭头切换字幕
  if (e.key === 'ArrowUp' && activeSubtitleIndex.value > 0) {
    e.preventDefault()
    setActiveSubtitle(activeSubtitleIndex.value - 1)
  }
  
  if (e.key === 'ArrowDown' && activeSubtitleIndex.value < subtitles.value.length - 1) {
    e.preventDefault()
    setActiveSubtitle(activeSubtitleIndex.value + 1)
  }
}

// 返回上一页
const goBack = () => {
  router.back()
}

// 工具函数
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`
}

const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString(locale.value)
}

const getHotWordStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    'ai_suggested': 'blue',
    'confirmed': 'green',
    'edited': 'orange'
  }
  return colors[status] || 'default'
}

const getHotWordStatusText = (status: string): string => {
  const keyMap: Record<string, string> = {
    ai_suggested: 'video.translationReviewPage.status.aiSuggested',
    confirmed: 'video.translationReviewPage.status.confirmed',
    edited: 'video.translationReviewPage.status.edited'
  }
  const key = keyMap[status]
  return key ? t(key) : t('video.translationReviewPage.status.unknown', { status })
}
</script>

<style lang="less" scoped>
.translation-review {
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    background: white;
    padding: 16px 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;

      .back-btn {
        padding: 4px 8px;
      }

      .title-section {
        h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .project-info {
          margin: 0;
          color: #666;
          font-size: 14px;
        }
      }
    }
  }

  /* 工作流步骤样式 */
  .workflow-steps-container {
    margin-bottom: 24px;
  }

  .workflow-steps {
    background: white;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    max-width: 100%;
  }

  .workflow-steps :deep(.ant-steps-item-title) {
    font-size: 16px;
    font-weight: 500;
  }

  .workflow-steps :deep(.ant-steps-item-description) {
    font-size: 12px;
    color: #666;
  }



  .main-content {
    .subtitle-card {
      height: calc(100vh - 200px);
      
      :deep(.ant-card-body) {
        height: calc(100% - 57px);
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .card-header {
        width: 100%;
        
        .card-title-section {
          display: flex;
          align-items: center;
          gap: 24px;
          
          .card-title {
            font-size: 16px;
            font-weight: 600;
            color: #333;
          }
          
          .character-stats-inline {
            display: flex;
            gap: 20px;
            align-items: center;
            
            .stats-item {
              display: flex;
              align-items: center;
              gap: 6px;
              
              .stats-label {
                font-size: 12px;
                color: #666;
                font-weight: 500;
              }
              
              .stats-value {
                font-size: 14px;
                color: #333;
                font-weight: 600;
              }
            }
          }
        }
      }

      .subtitle-list {
        flex: 1;
        overflow-y: auto;
        padding-right: 8px;

        .subtitle-item {
          border: 1px solid #e8e8e8;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.3s;
          background: #fff;
          position: relative;

          &:hover {
            border-color: #1890ff;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
          }

          &.active {
            border-color: #1890ff;
            background: #f0f8ff;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
          }

          .subtitle-time {
            font-size: 12px;
            color: #666;
            margin-bottom: 8px;
            font-family: monospace;
            font-weight: 500;
            padding: 2px 6px;
            background: #f8f9fa;
            border-radius: 3px;
            display: inline-block;
          }

            .subtitle-content {
              .original-text {
                margin-bottom: 8px;

                label {
                  display: inline-block;
                  width: 50px;
                  font-size: 12px;
                  color: #666;
                  font-weight: 500;
                }

                span {
                  color: #333;
                  font-size: 13px;
                  line-height: 1.4;
                }
              }

              .translated-text {
                margin-bottom: 8px;

                .translation-header {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-bottom: 4px;

                  label {
                    font-size: 12px;
                    color: #666;
                    font-weight: 500;
                    margin: 0;
                  }

                  .multiline-checkbox {
                    font-size: 12px;
                  }
                }

                .translation-input-main {
                  width: 100%;
                  margin-bottom: 6px;
                  border-radius: 3px;
                  border: 1px solid #d9d9d9;
                  transition: border-color 0.3s;
                  height: 32px;
                  resize: none;

                  &:focus {
                    border-color: #1890ff;
                    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                  }
                }

                .translation-input-multiline {
                  width: 100%;
                  border-radius: 3px;
                  border: 1px solid #d9d9d9;
                  transition: border-color 0.3s;
                  height: 32px;
                  resize: none;

                  &:focus {
                    border-color: #1890ff;
                    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                  }
                }
              }
          }
        }
      }
    }

    .hotword-card {
      height: calc(100vh - 200px);

      :deep(.ant-card-body) {
        height: calc(100% - 57px);
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        padding: 12px;
      }

      .hotword-stats {
        margin-bottom: 4px;
        
        :deep(.ant-statistic) {
          .ant-statistic-title {
            font-size: 12px;
            margin-bottom: 2px;
          }
          
          .ant-statistic-content {
            font-size: 16px;
            line-height: 1.1;
          }
        }
      }

      .hotword-list {
        flex: 1;
        margin-bottom: 8px;
        margin-top: 4px;
        min-height: 500px;

        :deep(.ant-table) {
          .ant-table-thead > tr > th {
            background: #fafafa;
            font-weight: 600;
            font-size: 12px;
            padding: 8px 4px;
          }
          
          .ant-table-tbody {
            min-height: 450px;
          }
          
          .ant-table-tbody > tr > td {
            padding: 8px 4px;
            font-size: 12px;
          }
          
          .ant-table-tbody > tr:hover > td {
            background: #f5f5f5;
          }
        }

        .hotword-text {
          font-weight: 500;
          color: #333;
        }

        .translated-word-clickable {
          cursor: pointer;
          padding: 2px 4px;
          border-radius: 4px;
          transition: background 0.3s;
          display: block;
          min-height: 20px;

          &:hover {
            background: #f0f8ff;
            color: #1890ff;
          }

          &.disabled {
            cursor: not-allowed;
            color: #999;
            
            &:hover {
              background: transparent;
              color: #999;
            }
          }
        }
      }

      .hotword-actions {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #f0f0f0;
      }
    }

  }
}

// 响应式设计
@media (max-width: 1200px) {
  .translation-review {
    .main-content {
      .subtitle-card,
      .hotword-card {
        height: auto;
        min-height: 500px;
      }
    }
  }
}
</style>